import { useEffect, useState } from "react";

export interface KeyboardInset {
  /** ソフトキーボードがビューポート下端を覆っている量 (px)。 */
  bottom: number;
  /** 実際に見えている高さ (px)。購読していない間は 0。 */
  height: number;
}

const NO_INSET: KeyboardInset = { bottom: 0, height: 0 };

/**
 * visualViewport の値から、下端が覆われている量と可視高さを求める。
 *
 * iOS Safari はソフトキーボードを開いても layout viewport (innerHeight) を変えず、
 * visual viewport だけを縮める。`bottom: 0` で置いた要素はキーボードの裏に入り、
 * svh も縮まないため、覆われた量を自分で求めて位置と高さへ反映する。
 *
 * offsetTop (visual viewport のずれ) はあえて見ない。iOS は入力欄を見せるために
 * visual viewport を一時的に下へずらすが、その分を差し引いて要素を追いかけると、
 * 要素が下がる→入力欄が隠れる→iOS がさらにずらす、の追いかけっこになる。
 * 覆われた量だけ持ち上げて要素がキーボードの上へ収まれば、iOS はずらす必要が
 * なくなり offsetTop は 0 へ戻る。
 */
export function calculateKeyboardInset(
  innerHeight: number,
  viewportHeight: number,
  scale: number,
): KeyboardInset {
  // 拡大中は visual viewport の CSS px が縮む。layout px へ戻してから差を取り、
  // ピンチ拡大をキーボードと取り違えないようにする。
  const visibleHeight = viewportHeight * scale;
  return {
    bottom: Math.max(0, Math.round(innerHeight - visibleHeight)),
    height: Math.max(0, Math.round(viewportHeight)),
  };
}

/**
 * visualViewport の変化を購読し、下端の覆われ量を返す。
 *
 * `active` が false の間は購読しない。ボトムシートは閉じていても DOM へ残るため、
 * 開いているものだけがリスナーを持つようにして、通常のスクロール中に
 * 何本ものリスナーが走る状態を避ける。
 */
export function useKeyboardInset(active: boolean): KeyboardInset {
  const [inset, setInset] = useState<KeyboardInset>(NO_INSET);

  useEffect(() => {
    const viewport = active ? (globalThis.visualViewport ?? null) : null;
    if (!viewport) {
      setInset(NO_INSET);
      return;
    }

    let frame = 0;
    const update = (): void => {
      cancelAnimationFrame(frame);
      // iOSはツールバーの伸縮中にもscrollを連続発火させるため、フレームへ束ねる
      frame = requestAnimationFrame(() => {
        const next = calculateKeyboardInset(
          window.innerHeight,
          viewport.height,
          viewport.scale,
        );
        // 求める値はビューポートのずれに依らないため、キーボードの開閉以外では
        // 同じ値になり再レンダリングも起きない。
        setInset((previous) =>
          previous.bottom === next.bottom && previous.height === next.height
            ? previous
            : next,
        );
      });
    };

    update();
    viewport.addEventListener("resize", update);
    viewport.addEventListener("scroll", update);
    return (): void => {
      cancelAnimationFrame(frame);
      viewport.removeEventListener("resize", update);
      viewport.removeEventListener("scroll", update);
    };
  }, [active]);

  return inset;
}
